import { create } from 'zustand';
import type { AuthUser } from './types';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';

interface SessionState {
  user: AuthUser | null;
  status: SessionStatus;
  setSession: (user: AuthUser | null) => void;
  clear: () => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  user: null,
  status: 'loading',
  setSession: (user) => set({ user, status: user ? 'authenticated' : 'anonymous' }),
  clear: () => set({ user: null, status: 'anonymous' }),
}));

/**
 * Read-only view of the current session. Selectors are subscribed individually so a
 * status change does not re-render components that only care about the user.
 */
export function useSession() {
  const user = useSessionStore((state) => state.user);
  const status = useSessionStore((state) => state.status);

  return {
    user,
    status,
    isAuthenticated: status === 'authenticated',
    hasPermission: (permission: string) => user?.permissions.includes(permission) ?? false,
    hasRole: (role: string) => user?.roles.includes(role) ?? false,
  };
}
